/**
 * QueryProvider - TanStack Query Provider Wrapper
 * 
 * Wraps the application with QueryClientProvider and ReactQueryDevtools.
 * This provider enables TanStack Query data fetching throughout the app.
 * 
 * @example
 * ```tsx
 * import QueryProvider from '@/modules/shared/providers/QueryProvider'
 * 
 * function App() {
 *   return (
 *     <QueryProvider>
 *       <YourApp />
 *     </QueryProvider>
 *   )
 * }
 * ```
 */
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { queryClient } from '@/modules/shared/lib/queryClient'
import type { ReactNode } from 'react'

interface QueryProviderProps {
  children: ReactNode
}

/**
 * Determines if the app is running in development mode
 */
const isDevelopment = import.meta.env.DEV

/**
 * QueryProvider wraps children with QueryClientProvider
 * and includes ReactQueryDevtools in development mode only.
 */
function QueryProvider({ children }: QueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {isDevelopment && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  )
}

export default QueryProvider
